import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Button } from "../../../../ui-kit/Button";
import { AddCircleIcon } from "../../../../components/icons/AddCircleIcon";
import { getPlaceTours } from "../../../../store/visitWidgetApi";
import { NavLink } from "../../../../modules/navigation/NavLink/NavLink";

import "./TourList.css";

interface TourListItemProps {
  /**
   * Number of stops on Tour.
   */
  numberOfStops: number;
  /**
   * Name of Tour.
   */
  name: string;
  /**
   * Category Ids of Tour.
   */
  categoryIds: number[];
  /**
   * Cover photo of Tour.
   */
  coverPhotoUrl: string;
  /**
   * Tour id.
   */
  id: number;
  /**
   * Tour slug.
   */
  slug: string;
}

const getIconForCategory = (id: number) => {
  const category = VisitWidget.store.categories.find((x) => x.id === id);
  return category?.color_svg_icon_url ?? "";
};

const TourListItem: FunctionComponent<TourListItemProps> = ({
  categoryIds,
  name,
  numberOfStops,
  coverPhotoUrl,
  slug,
}) => {
  const handleAddRemovePress = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.stopPropagation();
      // dispatch(addTourToPlanAction(id) as any);
    },
    [],
  );

  return (
    <NavLink to={`/tours/${slug}`} className="tour-list-item-row">
      <div className="tour-list-item-photo-container">
        <div
          className="tour-list-item-photo"
          style={{ backgroundImage: `url(${coverPhotoUrl})` }}></div>
        <div className="tour-list-item-categories">
          {categoryIds.slice(0, 2).map((categoryId) => {
            return (
              <span key={categoryId}>
                <img src={getIconForCategory(categoryId)} />
              </span>
            );
          })}
        </div>
      </div>
      <div className="tour-list-item-description">
        <div className="tour-list-item-description-title">{name}</div>
        <div className="tour-list-item-description-stops">
          {numberOfStops} Stop{numberOfStops > 1 ? "s" : ""}
        </div>
      </div>
      <div className="tour-list-item-actions">
        <Button
          type="icon"
          icon={<AddCircleIcon />}
          onPress={handleAddRemovePress}
        />
      </div>
    </NavLink>
  );
};

/**
 * List tours for the given entity. This component should only be reachable if the entity has tours.
 */
const TourList: FunctionComponent<TourListProps> = ({ id }) => {
  const [tours, setTours] = useState<TourListItemProps[] | undefined>();
  useEffect(() => {
    const fetchTours = async () => {
      const tourDTOs = await getPlaceTours(id);
      const mappedTours = tourDTOs.map((tour) => {
        return {
          categoryIds: tour.categoryIds,
          id: tour.id,
          coverPhotoUrl: tour.coverPhotoUrl,
          name: tour.name,
          numberOfStops: tour.planItems?.length ?? 0,
          slug: tour.slug,
        };
      });
      setTours(mappedTours);
    };

    fetchTours();
  }, [id]);

  if (tours === undefined) {
    return (
      <div className="tour-list-loading">
        <img src="/images/ajax-loader.gif" />
      </div>
    );
  }

  return (
    <div>
      {tours.map((tourListItem) => {
        return <TourListItem key={tourListItem.id} {...tourListItem} />;
      })}
    </div>
  );
};

interface TourListProps {
  /**
   * The `id` of the entity that appears on tours.
   */
  id: number;
  /**
   * The `type` of the entity that appears on tours.
   */
  type: "Place" | "Event";
}

export { TourList };
